import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Plus, Settings, Trash2, Copy, LogOut,
  MessageSquare, BarChart3, Zap, ArrowUpRight, CreditCard, History,
  Rocket, CheckCircle2, Circle, ShoppingCart
} from 'lucide-react';

import { API, EMBED_DOMAIN } from '@/config';
import { CardSkeleton, ListItemSkeleton } from '@/components/Skeleton';

const PLAN_COLORS = {
  free: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
  single: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  starter: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  pro: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
  business: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [widgets, setWidgets] = useState([]);
  const [usage, setUsage] = useState(null);
  const [plans, setPlans] = useState({});
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [topupBalance, setTopupBalance] = useState(null);
  const [buyingTopup, setBuyingTopup] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [w, u, p] = await Promise.all([
        axios.get(`${API}/widgets`),
        axios.get(`${API}/usage`),
        axios.get(`${API}/plans`),
      ]);
      setWidgets(w.data);
      setUsage(u.data);
      setPlans(p.data);
      // Load topup balance for free plan
      try {
        const tb = await axios.get(`${API}/topup/balance`);
        setTopupBalance(tb.data);
      } catch { /* Non-free plans don't have topup */ }
    } catch {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const createWidget = async () => {
    if (!newName.trim()) { toast.error('Enter a bot name'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/widgets`, { bot_name: newName.trim() });
      setNewName('');
      setShowCreate(false);
      toast.success('Widget created!');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create widget');
    } finally {
      setCreating(false);
    }
  };

  const deleteWidget = async (widgetId) => {
    if (!window.confirm('Delete this widget? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/widgets/${widgetId}`);
      toast.success('Widget deleted');
      loadData();
    } catch {
      toast.error('Failed to delete widget');
    }
  };

  const copyEmbed = (widgetId) => {
    const code = `<script src="${EMBED_DOMAIN}/api/embed/widget.js?id=${widgetId}"></script>`;
    navigator.clipboard.writeText(code);
    toast.success('Embed code copied to clipboard!');
  };

  const usagePercent = usage ? Math.min(100, Math.round((usage.messages_used / (topupBalance?.total_limit || usage.messages_limit || 1)) * 100)) : 0;

  const buyTopup = async () => {
    setBuyingTopup(true);
    try {
      const res = await axios.post(`${API}/topup/create`);
      const { order_id, razorpay_key_id } = res.data;
      // Load Razorpay script
      if (!document.getElementById('razorpay-script')) {
        const s = document.createElement('script');
        s.id = 'razorpay-script';
        s.src = 'https://checkout.razorpay.com/v1/checkout.js';
        document.body.appendChild(s);
        await new Promise(r => { s.onload = r; });
      }
      const rzp = new window.Razorpay({
        key: razorpay_key_id,
        order_id: order_id,
        name: 'Whisper.AI',
        description: '15 Messages Top-up',
        handler: async (response) => {
          try {
            await axios.post(`${API}/topup/verify`, {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success('15 messages added!');
            loadData();
          } catch { toast.error('Payment verification failed'); }
          setBuyingTopup(false);
        },
        modal: { ondismiss: () => setBuyingTopup(false) },
        prefill: { name: user?.name, email: user?.email },
        theme: { color: '#10B981' },
      });
      rzp.on('payment.failed', () => { toast.error('Payment failed'); setBuyingTopup(false); });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start payment');
      setBuyingTopup(false);
    }
  };
  const currentPlan = plans[user?.plan] || {};

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md bg-[#10B981] flex items-center justify-center">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="3" y="10" width="3" height="8" rx="1.5" fill="white" opacity="0.7" /><rect x="8" y="6" width="3" height="16" rx="1.5" fill="white" /><rect x="13" y="3" width="3" height="18" rx="1.5" fill="white" /><rect x="18" y="8" width="3" height="10" rx="1.5" fill="white" opacity="0.7" /></svg>
            </div>
            <span className="text-sm font-bold tracking-tight text-foreground">Whisper.AI</span>
          </div>
        </header>
        <main className="max-w-5xl mx-auto px-6 py-8 space-y-4">
          <div className="grid sm:grid-cols-5 gap-4"><CardSkeleton /><CardSkeleton /><CardSkeleton /><CardSkeleton /><CardSkeleton /></div>
          <ListItemSkeleton /><ListItemSkeleton />
        </main>
      </div>
    );
  }

  const userInitials = user?.name ? user.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '?';

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      {/* Top bar */}
      <header className="border-b border-border px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-[#10B981] flex items-center justify-center">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="3" y="10" width="3" height="8" rx="1.5" fill="white" opacity="0.7" /><rect x="8" y="6" width="3" height="16" rx="1.5" fill="white" /><rect x="13" y="3" width="3" height="18" rx="1.5" fill="white" /><rect x="18" y="8" width="3" height="10" rx="1.5" fill="white" opacity="0.7" /></svg>
          </div>
          <span className="text-sm font-bold tracking-tight text-foreground">Whisper.AI</span>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PLAN_COLORS[user?.plan] || PLAN_COLORS.free}`} data-testid="user-plan-badge">
            {currentPlan.name || 'Free'}
          </span>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-[#10B981]/15 flex items-center justify-center text-[10px] font-bold text-[#10B981]" data-testid="user-avatar">
              {userInitials}
            </div>
            <span className="text-xs text-muted-foreground hidden sm:block">{user?.name || user?.email}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }} data-testid="logout-btn">
            <LogOut className="h-4 w-4 mr-1" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Usage overview */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8" data-testid="usage-overview">
          <div className="p-5 rounded-xl border border-border bg-card">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <MessageSquare className="h-4 w-4" />
              <span className="text-xs font-medium uppercase tracking-wider">Messages</span>
            </div>
            <div className="text-2xl font-bold text-foreground">
              {usage?.messages_used?.toLocaleString() || 0}
              <span className="text-sm font-normal text-muted-foreground"> / {(topupBalance?.total_limit || usage?.messages_limit || 0).toLocaleString()}</span>
            </div>
            <div className="mt-3 w-full h-1.5 bg-muted rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${usagePercent}%`, background: usagePercent > 80 ? '#ef4444' : '#10B981' }} />
            </div>
            {topupBalance?.topup_enabled && (
              <div className="mt-2 flex items-center justify-between">
                {topupBalance.topup_balance > 0 && (
                  <span className="text-[10px] text-[#10B981]">+{topupBalance.topup_balance} top-up msgs</span>
                )}
                <button
                  onClick={buyTopup}
                  disabled={buyingTopup}
                  className="text-[10px] text-[#10B981] hover:underline font-medium flex items-center gap-1"
                  data-testid="buy-topup-btn"
                >
                  <ShoppingCart className="h-3 w-3" /> {buyingTopup ? 'Processing...' : 'Buy 15 msgs — $1'}
                </button>
              </div>
            )}
          </div>
          <div className="p-5 rounded-xl border border-border bg-card">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Zap className="h-4 w-4" />
              <span className="text-xs font-medium uppercase tracking-wider">Chat Bots</span>
            </div>
            <div className="text-2xl font-bold text-foreground">
              {widgets.length}
              <span className="text-sm font-normal text-muted-foreground"> / {currentPlan.widgets_limit === 999 ? '∞' : currentPlan.widgets_limit || 1}</span>
            </div>
          </div>
          <div className="p-5 rounded-xl border border-border bg-card">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <BarChart3 className="h-4 w-4" />
              <span className="text-xs font-medium uppercase tracking-wider">Plan</span>
            </div>
            <div className="text-2xl font-bold text-foreground">{currentPlan.name || 'Free'}</div>
            <Link to="/pricing" className="text-xs text-[#10B981] hover:underline inline-flex items-center gap-0.5 mt-1" data-testid="upgrade-link">
              Upgrade <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <Link to="/dashboard/billing" className="p-5 rounded-xl border border-border bg-card hover:border-[#10B981]/30 transition-colors group" data-testid="billing-card-link">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <CreditCard className="h-4 w-4" />
              <span className="text-xs font-medium uppercase tracking-wider">Billing</span>
            </div>
            <div className="text-sm font-semibold text-foreground">
              {currentPlan.price > 0 ? `$${currentPlan.price}/mo` : 'Free'}
            </div>
            <span className="text-xs text-muted-foreground group-hover:text-[#10B981] inline-flex items-center gap-0.5 mt-1 transition-colors">
              Invoices & details <ArrowUpRight className="h-3 w-3" />
            </span>
          </Link>
          <Link to="/dashboard/chat-history" className="p-5 rounded-xl border border-border bg-card hover:border-[#10B981]/30 transition-colors group" data-testid="chat-history-card-link">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <History className="h-4 w-4" />
              <span className="text-xs font-medium uppercase tracking-wider">Chat History</span>
            </div>
            <div className="text-sm font-semibold text-foreground">
              View Chats
            </div>
            <span className="text-xs text-muted-foreground group-hover:text-[#10B981] inline-flex items-center gap-0.5 mt-1 transition-colors">
              Conversations <ArrowUpRight className="h-3 w-3" />
            </span>
          </Link>
        </div>

        {/* Onboarding checklist — show when no widgets or no messages */}
        {widgets.length === 0 && (
          <div className="mb-8 p-6 rounded-2xl border border-[#10B981]/20 bg-[#10B981]/5" data-testid="onboarding-checklist">
            <div className="flex items-center gap-3 mb-4">
              <Rocket className="h-5 w-5 text-[#10B981]" />
              <h3 className="text-sm font-semibold text-foreground">Get started in 3 steps</h3>
            </div>
            <div className="space-y-3">
              {[
                { done: true, label: 'Create your account', desc: 'You\'re in!' },
                { done: widgets.length > 0, label: 'Create your first chat bot', desc: 'Click "New Chat Bot" below to get started', action: () => setShowCreate(true), actionLabel: 'Create Bot' },
                { done: false, label: 'Embed on your website', desc: 'Copy the one-line script and paste it on your site' },
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-3">
                  {step.done ? (
                    <CheckCircle2 className="h-5 w-5 text-[#10B981] shrink-0 mt-0.5" />
                  ) : (
                    <Circle className="h-5 w-5 text-muted-foreground/40 shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className={`text-sm ${step.done ? 'text-muted-foreground line-through' : 'text-foreground font-medium'}`}>{step.label}</p>
                    <p className="text-xs text-muted-foreground">{step.desc}</p>
                  </div>
                  {step.action && !step.done && (
                    <Button size="sm" className="bg-[#10B981] hover:bg-[#059669] text-white text-xs shrink-0" onClick={step.action}>
                      {step.actionLabel}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Widget list */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground" data-testid="widgets-heading">Your Chat Bots</h2>
          <Button size="sm" onClick={() => setShowCreate(true)} className="gap-1.5" data-testid="create-widget-btn">
            <Plus className="h-4 w-4" /> New Chat Bot
          </Button>
        </div>

        {showCreate && (
          <div className="p-4 rounded-xl border border-border bg-card mb-4 flex gap-3 items-end" data-testid="create-widget-form">
            <div className="flex-1">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Bot Name</label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g., Support Bot" onKeyDown={(e) => e.key === 'Enter' && createWidget()} data-testid="new-widget-name-input" />
            </div>
            <Button onClick={createWidget} disabled={creating} data-testid="confirm-create-widget-btn">
              {creating ? 'Creating...' : 'Create'}
            </Button>
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
          </div>
        )}

        {widgets.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border rounded-xl" data-testid="empty-widgets">
            <MessageSquare className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-40" />
            <p className="text-sm text-muted-foreground">No chat bots yet. Create your first one!</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="widgets-list">
            {widgets.map((w) => (
              <div key={w.widget_id} className="p-4 rounded-xl border border-border bg-card flex items-center justify-between group hover:border-[#10B981]/30 transition-colors" data-testid={`widget-card-${w.widget_id}`}>
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${w.accent_color || '#10B981'}20` }}>
                    <MessageSquare className="h-5 w-5" style={{ color: w.accent_color || '#10B981' }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{w.bot_name}</p>
                    <p className="text-xs text-muted-foreground">ID: {w.widget_id} &middot; {w.messages_used || 0} msgs this month</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyEmbed(w.widget_id)} title="Copy embed code" data-testid={`copy-embed-${w.widget_id}`}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/dashboard/widgets/${w.widget_id}`)} title="Configure" data-testid={`configure-widget-${w.widget_id}`}>
                    <Settings className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => deleteWidget(w.widget_id)} title="Delete" data-testid={`delete-widget-${w.widget_id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
